import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { login } from "../../Services/users/users.service";
import {
  LOGOUT_NOTICE_EVENT,
  dismissLogoutNotice,
  readLogoutNotice,
} from "../../Services/auth/session.service";
import { EMAIL_REGEX } from "../../Constants/regex";
import {
  EMAIL_REQUIRED,
  INVALID_EMAIL_MESSAGE,
  LOGIN_FAILED_MESSAGE,
  NETWORK_ERROR_MESSAGE,
  PASSWORD_REQUIRED,
} from "../../Constants/messages";
import { Paths } from "../../Constants/paths";
import { useMobile } from "../../hooks/deviceHooks/useDevice.hooks";
import { Colors } from "../../Constants/colors";
import {
  ALIGN_ITEMS,
  CURSOR,
  DISPLAY,
  FLEX_DIRECTION,
  FONT_SIZE,
  FONT_WEIGHT,
  JUSTIFY_CONTENT,
  SPACING,
} from "../../Constants/Enums/styles.enum";
import { FontFamily } from "../../Constants/Enums/FontFamily.enums";
import AuthInput from "./authInput";
import "./auth.css";

interface Props {
  header: string;
  subHeader: string;
}

type BannerTone = "error" | "notice";
type ErrorField = "email" | "password" | "form";

const ERROR_TINT = "#FFF6F6";

// The API owns the wording; we only decide which field the message belongs to.
const PASSWORD_HINTS = ["password", "credential"];
const EMAIL_HINTS = ["email", "account"];

const resolveErrorField = (message: string): ErrorField => {
  const normalized = message.toLowerCase();
  if (PASSWORD_HINTS.some((hint) => normalized.includes(hint))) return "password";
  if (EMAIL_HINTS.some((hint) => normalized.includes(hint))) return "email";
  return "form";
};

const EmailIcon = ({ color }: { color: string }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="2.5"
      y="4.5"
      width="19"
      height="15"
      rx="3"
      stroke={color}
      strokeWidth="1.8"
    />
    <path
      d="M3.5 7.5L12 13L20.5 7.5"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LockIcon = ({ color }: { color: string }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="4"
      y="10.5"
      width="16"
      height="10.5"
      rx="3"
      stroke={color}
      strokeWidth="1.8"
    />
    <path
      d="M8 10.5V7.5C8 5.29086 9.79086 3.5 12 3.5C14.2091 3.5 16 5.29086 16 7.5V10.5"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <circle cx="12" cy="15.75" r="1.4" fill={color} />
  </svg>
);

const BannerIcon = ({ tone }: { tone: BannerTone }) => {
  const color = tone === "error" ? Colors.Tomato : Colors.TangerineOrange;

  if (tone === "error") {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9.25" stroke={color} strokeWidth="1.8" />
        <path d="M12 7.5V13" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="12" cy="16.3" r="1.15" fill={color} />
      </svg>
    );
  }

  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9.25" stroke={color} strokeWidth="1.8" />
      <path
        d="M12 7.75V12.25L15 14.25"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const AlertBanner = ({
  tone,
  message,
  onDismiss,
}: {
  tone: BannerTone;
  message: string;
  onDismiss?: () => void;
}) => {
  const accent = tone === "error" ? Colors.Tomato : Colors.TangerineOrange;

  return (
    <div
      className="auth-banner"
      role={tone === "error" ? "alert" : "status"}
      style={{
        display: DISPLAY.FLEX,
        alignItems: ALIGN_ITEMS.START,
        gap: SPACING.S12,
        padding: `${SPACING.S12} ${SPACING.S14}`,
        borderRadius: "12px",
        border: `1px solid ${accent}40`,
        backgroundColor:
          tone === "error" ? ERROR_TINT : Colors.LIGHT_TANGERINE_ORANGE,
      }}
    >
      <span style={{ display: DISPLAY.FLEX, marginTop: "1px" }}>
        <BannerIcon tone={tone} />
      </span>
      <p
        style={{
          flex: 1,
          margin: 0,
          fontSize: FONT_SIZE.F13,
          fontFamily: FontFamily.ROBOTO_REGULAR,
          color: Colors.DarkGrey,
          lineHeight: "1.5",
        }}
      >
        {message}
      </p>
      {onDismiss ? (
        <button
          type="button"
          aria-label="Dismiss message"
          onClick={onDismiss}
          style={{
            border: "none",
            background: "transparent",
            padding: SPACING.S0,
            cursor: CURSOR.POINTER,
            color: Colors.Grey,
            fontSize: FONT_SIZE.F16,
            lineHeight: "1",
          }}
        >
          ×
        </button>
      ) : null}
    </div>
  );
};

const Login = ({ header, subHeader }: Props) => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [sessionNotice, setSessionNotice] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [bannerKey, setBannerKey] = useState<number>(0);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const isMobile = useMobile();
  const navigate = useNavigate();

  useEffect(() => {
    const syncNotice = () => setSessionNotice(readLogoutNotice());

    syncNotice();
    window.addEventListener(LOGOUT_NOTICE_EVENT, syncNotice);

    return () => {
      window.removeEventListener(LOGOUT_NOTICE_EVENT, syncNotice);
    };
  }, []);

  const clearErrors = () => {
    setEmailError(null);
    setPasswordError(null);
    setFormError(null);
  };

  const showBanner = (message: string) => {
    setBannerKey((key) => key + 1);
    setFormError(message);
  };

  const focusField = (field: ErrorField) => {
    const target = field === "password" ? passwordRef : emailRef;
    target.current?.focus();
    target.current?.select();
  };

  const validate = () => {
    const trimmedEmail = email.trim();
    let nextEmailError: string | null = null;
    let nextPasswordError: string | null = null;

    if (!trimmedEmail) {
      nextEmailError = EMAIL_REQUIRED;
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      nextEmailError = INVALID_EMAIL_MESSAGE;
    }

    if (!password) {
      nextPasswordError = PASSWORD_REQUIRED;
    }

    setEmailError(nextEmailError);
    setPasswordError(nextPasswordError);

    if (nextEmailError || nextPasswordError) {
      focusField(nextEmailError ? "email" : "password");
      return false;
    }

    return true;
  };

  const handleLoginFailure = (error: unknown) => {
    if (!axios.isAxiosError(error)) {
      showBanner(LOGIN_FAILED_MESSAGE);
      return;
    }

    if (!error.response) {
      showBanner(NETWORK_ERROR_MESSAGE);
      return;
    }

    const apiMessage = (error.response.data as { message?: string } | undefined)
      ?.message;

    if (!apiMessage) {
      showBanner(LOGIN_FAILED_MESSAGE);
      return;
    }

    // A 401 from the login endpoint means the account is not allowed to sign in
    // here (admin login), not that an existing session went stale.
    if (error.response.status === 401) {
      showBanner(apiMessage);
      return;
    }

    const field = resolveErrorField(apiMessage);

    if (field === "form") {
      showBanner(apiMessage);
      return;
    }

    if (field === "password") {
      setPasswordError(apiMessage);
    } else {
      setEmailError(apiMessage);
    }
    focusField(field);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    dismissLogoutNotice();
    setSessionNotice(null);
    clearErrors();

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await login({ email, password });
      navigate(Paths.Dashboard, { replace: true });
    } catch (error) {
      handleLoginFailure(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      style={{
        display: DISPLAY.FLEX,
        flexDirection: FLEX_DIRECTION.COLUMN,
        gap: isMobile ? SPACING.S24 : SPACING.S28,
      }}
    >
      {/* Heading */}
      <div>
        {!isMobile && (
          <p
            style={{
              margin: 0,
              marginBottom: SPACING.S12,
              fontSize: FONT_SIZE.F12,
              fontFamily: FontFamily.POPPINS_REGULAR,
              fontWeight: FONT_WEIGHT.SEMI_BOLD,
              letterSpacing: "2px",
              textTransform: "uppercase",
              color: Colors.TangerineOrange,
            }}
          >
            Rose Mount Pre School
          </p>
        )}
        <h1
          style={{
            margin: 0,
            marginBottom: SPACING.S8,
            fontSize: isMobile ? FONT_SIZE.F28 : FONT_SIZE.F32,
            fontFamily: FontFamily.POPPINS_BOLD,
            color: Colors.Black,
            letterSpacing: "-0.5px",
          }}
        >
          Welcome back
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: FONT_SIZE.F14,
            fontFamily: FontFamily.ROBOTO_REGULAR,
            color: Colors.Grey,
            lineHeight: "1.6",
          }}
        >
          Sign in with your school account to continue.
        </p>
      </div>

      {sessionNotice ? (
        <AlertBanner
          tone="notice"
          message={sessionNotice}
          onDismiss={() => {
            dismissLogoutNotice();
            setSessionNotice(null);
          }}
        />
      ) : null}

      {formError ? (
        <AlertBanner key={bannerKey} tone="error" message={formError} />
      ) : null}

      {/* Credentials */}
      <div
        style={{
          display: DISPLAY.FLEX,
          flexDirection: FLEX_DIRECTION.COLUMN,
          gap: SPACING.S20,
        }}
      >
        <AuthInput
          id="login-email"
          name="email"
          label="Email address"
          placeholder="you@rosemount.edu.np"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(value) => {
            setEmail(value);
            if (emailError) setEmailError(null);
          }}
          errorMessage={emailError}
          disabled={isSubmitting}
          inputRef={emailRef}
          renderIcon={(color) => <EmailIcon color={color} />}
        />

        <AuthInput
          id="login-password"
          name="password"
          label="Password"
          placeholder="Enter your password"
          autoComplete="current-password"
          isPassword
          value={password}
          onChange={(value) => {
            setPassword(value);
            if (passwordError) setPasswordError(null);
          }}
          errorMessage={passwordError}
          disabled={isSubmitting}
          inputRef={passwordRef}
          renderIcon={(color) => <LockIcon color={color} />}
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting}
        style={{
          display: DISPLAY.FLEX,
          alignItems: ALIGN_ITEMS.CENTER,
          justifyContent: JUSTIFY_CONTENT.CENTER,
          gap: SPACING.S10,
          width: "100%",
          height: "54px",
          border: "none",
          borderRadius: "14px",
          background: `linear-gradient(135deg, ${Colors.TangerineOrange} 0%, ${Colors.CrimsonRed} 100%)`,
          boxShadow: `0 6px 18px ${Colors.TangerineOrange}40`,
          cursor: isSubmitting ? CURSOR.NOT_ALLOWED : CURSOR.POINTER,
          opacity: isSubmitting ? 0.75 : 1,
          transition: "all 0.25s ease",
        }}
        onMouseEnter={(event) => {
          if (isSubmitting) return;
          event.currentTarget.style.transform = "translateY(-2px)";
          event.currentTarget.style.boxShadow = `0 10px 26px ${Colors.TangerineOrange}59`;
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.transform = "translateY(0)";
          event.currentTarget.style.boxShadow = `0 6px 18px ${Colors.TangerineOrange}40`;
        }}
      >
        {isSubmitting ? (
          <CircularProgress size={18} sx={{ color: Colors.White }} />
        ) : null}
        <span
          style={{
            fontSize: FONT_SIZE.F16,
            fontFamily: FontFamily.ROBOTO_BOLD,
            color: Colors.White,
            letterSpacing: "0.3px",
          }}
        >
          {isSubmitting ? "Signing in…" : "Sign in"}
        </span>
        {!isSubmitting ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M5 12H19M19 12L12.5 5.5M19 12L12.5 18.5"
              stroke={Colors.White}
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : null}
      </button>

      {/* Footer note */}
      <div
        style={{
          display: DISPLAY.FLEX,
          alignItems: ALIGN_ITEMS.CENTER,
          justifyContent: JUSTIFY_CONTENT.CENTER,
          gap: SPACING.S8,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 21.5C12 21.5 19.5 18 19.5 12V5.25L12 2.5L4.5 5.25V12C4.5 18 12 21.5 12 21.5Z"
            stroke={Colors.Grey}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span
          style={{
            fontSize: FONT_SIZE.F12,
            fontFamily: FontFamily.ROBOTO_REGULAR,
            color: Colors.Grey,
          }}
        >
          Encrypted connection · Your credentials stay private
        </span>
      </div>
    </form>
  );
};
export default Login;
